/**
 * OpenAI Ads Measurement Pixel adapter.
 * Maps domain `trackEvent` names onto OpenAI `oaiq("measure", ...)` events.
 * Mirrors `FacebookAdapter`'s shape so both ad pixels are wired the same way.
 * @see https://developers.openai.com/ads/measurement-pixel
 */

import { warnOnce } from './logger';
import { OPENAI_EVENT_MAP, EXCLUDED_AD_PIXEL_HOSTS } from '../../constants';
import type { OpenAiEventDTO } from './openai.dto';
import type { EventTracker } from '../../domain/interfaces/EventTracker';
import { generateEventId, queueAdServerOpenAiPart } from '../../utils/adServerEvent';
import { toOpenAiServerEventPayload } from '../../utils/openaiServerEventMapper';
import { toFacebookStringArray } from '../../utils/facebookCommonAttributes';
import {
  collectOpenAiUserSource,
  getOpenAiOppref,
  fingerprintOpenAiUserSource,
  overridesFromEventProperties,
  setCachedOpenAiHashedUser,
  toOpenAiPixelUser,
  type OpenAiUserOverrides,
} from '../../utils/openaiUserAttributes';

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

export interface OpenAiAdapterConfig {
  enabled?: boolean;
}

const OPENAI_EVENT_DEDUPE_MS = 2000;

interface OpenAiContentItem {
  id: string;
  name?: string;
  content_type?: string;
  quantity?: number;
  amount?: number;
  currency?: string;
}

function toIntegerAmount(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.round(parsed);
}

function compactRecord<T extends Record<string, unknown>>(record: T): T {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value != null && value !== ''),
  ) as T;
}

function buildOpenAiContents(properties: Record<string, unknown>): OpenAiContentItem[] {
  const ids = toFacebookStringArray(properties.content_ids);
  const names = toFacebookStringArray(properties.content_names);
  const contentType =
    typeof properties.content_type === 'string' && properties.content_type.trim()
      ? properties.content_type.trim()
      : 'product';
  const amount = toIntegerAmount(properties.value);
  const currency =
    typeof properties.currency === 'string' && properties.currency.trim()
      ? properties.currency.trim()
      : 'INR';

  const rawContents = properties.contents;
  if (Array.isArray(rawContents) && rawContents.length > 0) {
    return rawContents
      .map((item, index) => {
        const rec = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const id = rec.id != null && rec.id !== '' ? String(rec.id) : ids[index] ?? '';
        const quantityRaw = rec.quantity != null ? Number(rec.quantity) : 1;
        const quantity = Number.isFinite(quantityRaw) && quantityRaw > 0 ? Math.round(quantityRaw) : 1;
        const itemAmount = toIntegerAmount(rec.amount ?? rec.item_price ?? amount);
        return compactRecord({
          id,
          name: names[index] || (typeof rec.name === 'string' ? rec.name : undefined),
          content_type: contentType,
          quantity,
          amount: itemAmount,
          currency: itemAmount != null ? currency : undefined,
        }) as OpenAiContentItem;
      })
      .filter((item) => item.id);
  }

  return ids.map((id, index) =>
    compactRecord({
      id,
      name: names[index],
      content_type: contentType,
      quantity: 1,
      amount,
      currency: amount != null ? currency : undefined,
    }) as OpenAiContentItem,
  );
}

function buildPageContents(): OpenAiContentItem[] {
  if (typeof window === 'undefined') return [];
  const id = window.location.pathname || '/';
  const name = typeof document !== 'undefined' ? document.title?.trim() : '';
  return [compactRecord({ id, name: name || id, content_type: 'page' }) as OpenAiContentItem];
}

function contentsPayload(
  properties: Record<string, unknown>,
  contents: OpenAiContentItem[],
): Record<string, unknown> {
  const amount = toIntegerAmount(properties.value);
  const currency =
    typeof properties.currency === 'string' && properties.currency.trim()
      ? properties.currency.trim()
      : 'INR';

  return compactRecord({
    type: 'contents',
    amount,
    currency: amount != null ? currency : undefined,
    contents: contents.length > 0 ? contents : undefined,
  });
}

/** Builds the `oaiq("measure", ...)` call for a mapped domain event. */
function toOpenAiEventDTO(
  eventName: string,
  properties: Record<string, unknown>,
  eventId: string,
): OpenAiEventDTO | null {
  const mapping = OPENAI_EVENT_MAP[eventName];
  if (!mapping) return null;

  const options = compactRecord({
    event_id: eventId,
    oppref: getOpenAiOppref(),
  });

  if (mapping.oaiName === 'page_viewed') {
    return {
      eventName: 'page_viewed',
      data: contentsPayload(properties, buildPageContents()),
      options,
    };
  }

  if (mapping.oaiName === 'contents_viewed') {
    return {
      eventName: 'contents_viewed',
      data: contentsPayload(properties, buildOpenAiContents(properties)),
      options,
    };
  }

  if (mapping.oaiName === 'items_added') {
    return {
      eventName: 'items_added',
      data: contentsPayload(properties, buildOpenAiContents(properties)),
      options,
    };
  }

  if (mapping.oaiName === 'registration_completed') {
    return {
      eventName: 'registration_completed',
      data: { type: 'customer_action' },
      options,
    };
  }

  // 'custom'
  const contentsData = contentsPayload(properties, buildOpenAiContents(properties));
  return {
    eventName: 'custom',
    data: compactRecord({
      type: 'custom',
      amount: contentsData.amount,
      currency: contentsData.currency,
      contents: contentsData.contents,
    }),
    options: {
      ...options,
      custom_event_name: mapping.customEventName ?? 'custom_event',
    },
  };
}

/** Blocks Strict Mode remounts and loading-state re-runs from sending the same view twice. */
const recentOpenAiEventKeys = new Map<string, number>();

function wasOpenAiEventRecentlyFired(key: string): boolean {
  const now = Date.now();
  const previous = recentOpenAiEventKeys.get(key);
  if (previous != null && now - previous < OPENAI_EVENT_DEDUPE_MS) return true;
  recentOpenAiEventKeys.set(key, now);
  return false;
}

function openAiDedupeKey(eventName: string, properties: Record<string, unknown>): string | null {
  if (eventName === 'PageView') {
    return `PageView:${window.location.pathname}`;
  }
  if (eventName === 'PDP Page Viewed' || eventName === 'ViewContent') {
    const contentIds = toFacebookStringArray(properties.content_ids);
    const designId = properties.design_id != null ? String(properties.design_id) : '';
    const identity = contentIds[0] || designId;
    return identity ? `ViewContent:${identity}` : null;
  }
  return null;
}

function fireOpenAiMeasure(dto: OpenAiEventDTO): boolean {
  const oaiq = window.oaiq;
  if (typeof oaiq !== 'function') return false;

  try {
    if (dto.options && Object.keys(dto.options).length > 0) {
      oaiq('measure', dto.eventName, dto.data, dto.options);
    } else {
      oaiq('measure', dto.eventName, dto.data);
    }
    return true;
  } catch (error) {
    console.error(`OpenAI Pixel measure failed for '${dto.eventName}':`, error);
    return false;
  }
}

function getOpenAiQueue(): typeof window.oaiq | undefined {
  return typeof window !== 'undefined' && typeof window.oaiq === 'function' ? window.oaiq : undefined;
}

export class OpenAiAdapter implements EventTracker {
  private config: OpenAiAdapterConfig;
  private initialized = false;
  private lastUserFingerprint: string | null = null;
  private inFlightUserSync: Promise<void> | null = null;
  private inFlightFingerprint: string | null = null;

  constructor(config: OpenAiAdapterConfig = {}) {
    this.config = {
      enabled: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.config.enabled) return;
    this.initialized = true;
    if (typeof window !== 'undefined') {
      void this.syncUserAndInit();
    }
  }

  /**
   * Re-init the Pixel with hashed user matching fields after login/signup.
   * OpenAI user data is request-scoped on `init`, not on `measure`.
   */
  loginUser(
    userCode: string,
    attributes?: { email?: string; firstName?: string; phone?: string },
  ): void {
    if (!this.config.enabled || typeof window === 'undefined') return;
    void this.syncUserAndInit({
      userCode,
      email: attributes?.email,
      firstName: attributes?.firstName,
      phone: attributes?.phone,
    });
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.config.enabled) {
      warnOnce('openai:disabled', 'OpenAI Pixel adapter is disabled');
      return;
    }
    if (typeof window === 'undefined') return;
    if (EXCLUDED_AD_PIXEL_HOSTS.includes(window.location.hostname)) return;

    const attrs = properties ?? {};
    const dedupeKey = openAiDedupeKey(eventName, attrs);
    if (dedupeKey && wasOpenAiEventRecentlyFired(dedupeKey)) return;

    const dto = toOpenAiEventDTO(
      eventName,
      attrs,
      generateEventId(),
    );
    if (!dto) return;

    void this.dispatch(eventName, dto, overridesFromEventProperties(attrs));

    // Server POST reuses Meta user attrs (`em`, `ph`, `external_id`, …). Backend
    // maps those onto OpenAI CAPI — do not send a separate `user` object here.
    queueAdServerOpenAiPart(toOpenAiServerEventPayload(eventName, dto));
  }

  private async syncUserAndInit(
    overrides: OpenAiUserOverrides = {},
    options: { force?: boolean } = {},
  ): Promise<void> {
    const source = collectOpenAiUserSource(overrides);
    const fingerprint = fingerprintOpenAiUserSource(source);
    if (!options.force && fingerprint === this.lastUserFingerprint) return;
    if (this.inFlightUserSync && this.inFlightFingerprint === fingerprint) {
      await this.inFlightUserSync;
      return;
    }

    const run = (async () => {
      const user = await toOpenAiPixelUser(source);
      setCachedOpenAiHashedUser(user);

      const oaiq = getOpenAiQueue();
      if (!oaiq) return;
      // OpenAI matching fields are request-scoped on `init` (not `measure`).
      // Re-init immediately before each event with the same Meta user snapshot.
      oaiq('init', { user });
      this.lastUserFingerprint = fingerprint;
    })();

    this.inFlightFingerprint = fingerprint;
    this.inFlightUserSync = run;
    try {
      await run;
    } catch (error) {
      console.error('OpenAI Pixel user init failed:', error);
    } finally {
      if (this.inFlightUserSync === run) {
        this.inFlightUserSync = null;
        this.inFlightFingerprint = null;
      }
    }
  }

  private async dispatch(
    eventName: string,
    dto: OpenAiEventDTO,
    overrides: OpenAiUserOverrides,
  ): Promise<void> {
    await this.syncUserAndInit(overrides, { force: true });
    if (fireOpenAiMeasure(dto)) return;

    setTimeout(() => {
      void this.syncUserAndInit(overrides, { force: true }).then(() => {
        if (!fireOpenAiMeasure(dto)) {
          console.warn(`OpenAI Pixel is still not loaded. Event '${eventName}' was skipped.`);
        }
      });
    }, 1000);
  }
}
